import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, MagnifyingGlass, PencilSimple, Trash, X, Check, UserCirclePlus, Star, Shield, Crown, Envelope, Phone, WhatsappLogo, Wrench, Cpu, UploadSimple, UserCircle, Camera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "cliente", phone: "", whatsapp: "", position: "", policy_type_id: "" });
  const [editUser, setEditUser] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const { token } = useAuth();

  const fetchUsers = async () => {
    try {
      const [usersRes, polRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/policy-types`)
      ]);
      setUsers(usersRes.data);
      setPolicyTypes(polRes.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    }
  };

  useEffect(() => { fetchUsers(); }, [token]);

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/auth/register`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Usuario creado");
      setIsCreateOpen(false);
      setNewUser({ email: "", password: "", name: "", role: "cliente", phone: "", whatsapp: "", position: "", policy_type_id: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    }
  };

  const openEdit = (u) => {
    setEditUser({
      id: u.id,
      name: u.name,
      email: u.email,
      phone: u.phone || "",
      whatsapp: u.whatsapp || "",
      position: u.position || "",
      photo_url: u.photo_url || "",
      role: u.role,
      new_password: "",
      policy_type_id: u.policy_type_id || "",
      policy_activation_date: u.policy_activation_date || "",
      engineer_level: u.engineer_level || "prueba",
      engineer_level_date: u.engineer_level_date || "",
      engineer_monthly_goal: u.engineer_monthly_goal || "",
      seller_level: u.seller_level || "",
      seller_level_date: u.seller_level_date || "",
      seller_meta_prospectos: u.seller_meta_prospectos || "",
      seller_meta_proyectos: u.seller_meta_proyectos || "",
      seller_meta_rollos: u.seller_meta_rollos || ""
    });
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = { ...editUser };
      delete payload.id;
      if (!payload.new_password) delete payload.new_password;
      if (!payload.policy_activation_date) delete payload.policy_activation_date;
      await axios.put(`${API}/users/${editUser.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Usuario actualizado");
      setIsEditOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (error) {
      toast.error("Error al eliminar usuario");
    }
  };

  const handleUploadPhoto = async (userId, file) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/users/${userId}/photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      if (editUser && editUser.id === userId) {
        setEditUser(prev => ({ ...prev, photo_url: data.photo_url }));
      }
      toast.success("Foto actualizada");
      fetchUsers();
    } catch (error) {
      toast.error("Error al subir foto");
    } finally { setUploadingPhoto(false); }
  };

  const roleColors = {
    admin: "bg-purple-100 text-purple-800 border-purple-200",
    ingeniero: "bg-blue-100 text-blue-800 border-blue-200",
    cliente: "bg-slate-100 text-slate-800 border-slate-200",
    comercial: "bg-teal-100 text-teal-800 border-teal-200",
    auxiliar: "bg-amber-100 text-amber-800 border-amber-200",
    marketing: "bg-pink-100 text-pink-800 border-pink-200",
    subgerente: "bg-indigo-100 text-indigo-800 border-indigo-200"
  };

  return (
    <div className="form-section" data-testid="users-management">
      <div className="flex justify-between items-center mb-6">
        <h2 className="border-0 pb-0 mb-0">Gestión de Usuarios</h2>
        <Button className="btn-primary" onClick={() => setIsCreateOpen(true)} data-testid="add-user-button">
          <Plus size={18} className="mr-2" /> Nuevo Usuario
        </Button>
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="input-sharp mt-1" data-testid="new-user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="input-sharp mt-1" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="input-sharp mt-1" data-testid="new-user-password" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                <SelectTrigger className="input-sharp mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="ingeniero">Ingeniero</SelectItem>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="auxiliar">Auxiliar Administrativa</SelectItem>
                  <SelectItem value="subgerente">Subgerente</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} className="input-sharp mt-1" data-testid="new-user-phone" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={newUser.whatsapp} onChange={(e) => setNewUser({ ...newUser, whatsapp: e.target.value })} className="input-sharp mt-1" data-testid="new-user-whatsapp" />
              </div>
            </div>
            <div>
              <Label>Puesto / Cargo</Label>
              <Input value={newUser.position} onChange={(e) => setNewUser({ ...newUser, position: e.target.value })} className="input-sharp mt-1" placeholder="Ej: Gerente de Ventas" data-testid="new-user-position" />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" className="btn-secondary">Cancelar</Button></DialogClose>
            <Button onClick={handleCreateUser} className="btn-primary" data-testid="create-user-submit">Crear Usuario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              {/* Photo Upload */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="relative">
                  {editUser.photo_url ? (
                    <img src={`${API}/quotations/media/${editUser.photo_url.replace(/^quotations\//, "")}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-blue-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                      <UserCircle size={32} className="text-slate-300" />
                    </div>
                  )}
                  <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 shadow-md">
                    <Camera size={12} className="text-white" />
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingPhoto}
                      onChange={(e) => e.target.files?.[0] && handleUploadPhoto(editUser.id, e.target.files[0])} data-testid="edit-user-photo" />
                  </label>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-700">{editUser.name}</p>
                  <p className="text-xs text-slate-400">{uploadingPhoto ? "Subiendo foto..." : "Clic en el icono para cambiar la foto"}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Nombre</Label>
                  <Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} className="input-sharp mt-1 h-9 text-sm" data-testid="edit-user-name" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} className="input-sharp mt-1 h-9 text-sm" data-testid="edit-user-email" />
                </div>
                <div>
                  <Label className="text-xs">Rol</Label>
                  <Select value={editUser.role} onValueChange={(val) => setEditUser({ ...editUser, role: val })}>
                    <SelectTrigger className="input-sharp mt-1 h-9 text-sm" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cliente">Cliente</SelectItem>
                      <SelectItem value="ingeniero">Ingeniero</SelectItem>
                      <SelectItem value="comercial">Comercial</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="auxiliar">Auxiliar Administrativa</SelectItem>
                      <SelectItem value="subgerente">Subgerente</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Teléfono</Label>
                  <Input value={editUser.phone} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} className="input-sharp mt-1 h-9 text-sm" data-testid="edit-user-phone" />
                </div>
                <div>
                  <Label className="text-xs">WhatsApp</Label>
                  <Input value={editUser.whatsapp} onChange={(e) => setEditUser({ ...editUser, whatsapp: e.target.value })} className="input-sharp mt-1 h-9 text-sm" data-testid="edit-user-whatsapp" />
                </div>
                <div>
                  <Label className="text-xs">Puesto / Cargo</Label>
                  <Input value={editUser.position || ""} onChange={(e) => setEditUser({ ...editUser, position: e.target.value })} className="input-sharp mt-1 h-9 text-sm" placeholder="Ej: Gerente de Ventas" data-testid="edit-user-position" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Nueva Contraseña</Label>
                  <Input
                    type="password"
                    value={editUser.new_password}
                    onChange={(e) => setEditUser({ ...editUser, new_password: e.target.value })}
                    className="input-sharp mt-1 h-9 text-sm"
                    placeholder="Dejar vacío para no cambiar"
                    data-testid="edit-user-password"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">Solo llenar si deseas cambiar la contraseña</p>
                </div>
              </div>

              {/* Engineer Level Section - only for engineers */}
              {editUser.role === "ingeniero" && (
                <div className="border-t pt-3 mt-2">
                  <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Star size={14} weight="fill" /> Estatus de Ingeniero
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Nivel</Label>
                      <Select value={editUser.engineer_level} onValueChange={(val) => setEditUser({ ...editUser, engineer_level: val })}>
                        <SelectTrigger className="input-sharp mt-1 h-9 text-sm" data-testid="edit-engineer-level">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prueba">Estamos a prueba</SelectItem>
                          <SelectItem value="nivel_1">Nivel 1 — 2 estrellas</SelectItem>
                          <SelectItem value="nivel_2">Nivel 2 — 4 estrellas</SelectItem>
                          <SelectItem value="nivel_3">Nivel 3 — 5 estrellas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Fecha de Nivel</Label>
                      <Input
                        type="date"
                        value={editUser.engineer_level_date}
                        onChange={(e) => setEditUser({ ...editUser, engineer_level_date: e.target.value })}
                        className="input-sharp mt-1 h-9 text-sm"
                        data-testid="edit-engineer-level-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Meta del Mes</Label>
                      <Input
                        value={editUser.engineer_monthly_goal}
                        onChange={(e) => setEditUser({ ...editUser, engineer_monthly_goal: e.target.value })}
                        className="input-sharp mt-1 h-9 text-sm"
                        placeholder="Ej: Resolver 30 tickets"
                        data-testid="edit-engineer-monthly-goal"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Seller Level Section - only for comercial */}
              {editUser.role === "comercial" && (
                <div className="border-t pt-3 mt-2">
                  <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Star size={14} weight="fill" /> Estatus de Vendedor
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Nivel de Vendedor</Label>
                      <Select value={editUser.seller_level || ""} onValueChange={(val) => setEditUser({ ...editUser, seller_level: val })}>
                        <SelectTrigger className="input-sharp mt-1 h-9 text-sm" data-testid="edit-seller-level">
                          <SelectValue placeholder="Seleccionar nivel" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vendedor_prueba">Vendedor a Prueba — 1 estrella</SelectItem>
                          <SelectItem value="vendedor_jr">Vendedor Jr — 2 estrellas</SelectItem>
                          <SelectItem value="ejecutivo_cuenta">Ejecutivo de Cuenta — 3 estrellas</SelectItem>
                          <SelectItem value="gerente_sucursal">Gerente de Sucursal — 4 estrellas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Fecha de Nivel</Label>
                      <Input
                        type="date"
                        value={editUser.seller_level_date}
                        onChange={(e) => setEditUser({ ...editUser, seller_level_date: e.target.value })}
                        className="input-sharp mt-1 h-9 text-sm"
                        data-testid="edit-seller-level-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Meta de Prospectos</Label>
                      <Input
                        value={editUser.seller_meta_prospectos}
                        onChange={(e) => setEditUser({ ...editUser, seller_meta_prospectos: e.target.value })}
                        className="input-sharp mt-1 h-9 text-sm"
                        placeholder="Ej: 20 prospectos"
                        data-testid="edit-seller-meta-prospectos"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div>
                      <Label className="text-xs">Meta de Proyectos</Label>
                      <Input
                        value={editUser.seller_meta_proyectos}
                        onChange={(e) => setEditUser({ ...editUser, seller_meta_proyectos: e.target.value })}
                        className="input-sharp mt-1 h-9 text-sm"
                        placeholder="Ej: 5 proyectos"
                        data-testid="edit-seller-meta-proyectos"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Meta de Rollos</Label>
                      <Input
                        value={editUser.seller_meta_rollos}
                        onChange={(e) => setEditUser({ ...editUser, seller_meta_rollos: e.target.value })}
                        className="input-sharp mt-1 h-9 text-sm"
                        placeholder="Ej: 10 rollos"
                        data-testid="edit-seller-meta-rollos"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" className="btn-secondary">Cancelar</Button></DialogClose>
            <Button onClick={handleSaveEdit} className="btn-primary" data-testid="save-edit-user-submit">Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <table className="ticket-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Email</th>
            <th>Rol</th>
            <th>Nivel</th>
            <th>Teléfono</th>
            <th>Meta del Mes</th>
            <th>Creado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const levelLabels = { prueba: "A prueba", nivel_1: "Nivel 1", nivel_2: "Nivel 2", nivel_3: "Nivel 3" };
            const levelStars = { prueba: 0, nivel_1: 2, nivel_2: 4, nivel_3: 5 };
            const starCount = levelStars[u.engineer_level] || 0;
            const sellerLevelLabels = { vendedor_prueba: "Vendedor a Prueba", vendedor_jr: "Vendedor Jr", ejecutivo_cuenta: "Ejecutivo de Cuenta", gerente_sucursal: "Gerente de Sucursal" };
            const sellerLevelStars = { vendedor_prueba: 1, vendedor_jr: 2, ejecutivo_cuenta: 3, gerente_sucursal: 4 };
            const sellerStarCount = sellerLevelStars[u.seller_level] || 0;
            return (
            <tr key={u.id} data-testid={`user-row-${u.id}`}>
              <td>
                <div className="flex items-center gap-2">
                  {u.photo_url ? (
                    <img src={`${API}/quotations/media/${u.photo_url.replace(/^quotations\//, "")}`} alt="" className="w-8 h-8 rounded-full object-cover border" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <UserCircle size={20} className="text-slate-300" />
                    </div>
                  )}
                  <div>
                    <span className="font-medium">{u.name}</span>
                    {u.position && <span className="block text-xs text-slate-400">{u.position}</span>}
                  </div>
                </div>
              </td>
              <td>{u.email}</td>
              <td>
                <span className={`px-2 py-1 text-xs font-medium border ${roleColors[u.role]}`}>
                  {u.role}
                </span>
              </td>
              <td>
                {u.role === "ingeniero" && u.engineer_level ? (
                  <div className="flex flex-col gap-1" data-testid={`engineer-level-${u.id}`}>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 border border-blue-200 rounded">
                      {starCount > 0 && [...Array(starCount)].map((_, i) => (
                        <Star key={i} size={10} weight="fill" className="text-amber-400" />
                      ))}
                      {levelLabels[u.engineer_level]}
                    </span>
                    {u.engineer_level_date && <span className="text-[10px] text-slate-400">Desde: {u.engineer_level_date}</span>}
                  </div>
                ) : u.role === "comercial" && u.seller_level ? (
                  <div className="flex flex-col gap-1" data-testid={`seller-level-${u.id}`}>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 bg-sky-50 px-2 py-1 border border-sky-200 rounded">
                      {sellerStarCount > 0 && [...Array(sellerStarCount)].map((_, i) => (
                        <Star key={i} size={10} weight="fill" className="text-sky-500" />
                      ))}
                      {sellerLevelLabels[u.seller_level]}
                    </span>
                    {u.seller_level_date && <span className="text-[10px] text-slate-400">Desde: {u.seller_level_date}</span>}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </td>
              <td>{u.phone || "—"}</td>
              <td className="text-xs text-slate-600 max-w-[200px] truncate">
                {u.role === "ingeniero" && u.engineer_monthly_goal ? u.engineer_monthly_goal : 
                 u.role === "comercial" && u.seller_meta_prospectos ? `P:${u.seller_meta_prospectos} | Pr:${u.seller_meta_proyectos || "—"} | R:${u.seller_meta_rollos || "—"}` : "—"}
              </td>
              <td className="font-mono text-xs text-slate-500">
                {format(new Date(u.created_at), "dd/MM/yyyy")}
              </td>
              <td>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-blue-600 hover:bg-blue-50" data-testid={`edit-user-${u.id}`}>
                    <PencilSimple size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SoftwareTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: "", description: "", category: "" });
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const { token } = useAuth();

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/software-types`);
      setTypes(response.data);
    } catch (error) {
      toast.error("Error al cargar tipos de software");
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const categories = [...new Set(types.map(t => t.category).filter(Boolean))];
  const filtered = filterCategory === "all" ? types : types.filter(t => (t.category || "Sin categoría") === filterCategory);
  const grouped = filtered.reduce((acc, t) => {
    const cat = t.category || "Sin categoría";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const handleQuickAdd = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/software-types`, { name: newName.trim(), category: newCategory.trim() || null, description: "" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Software agregado");
      setNewName("");
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleQuickAdd();
  };

  const startEdit = (type) => {
    setEditId(type.id);
    setEditData({ name: type.name, description: type.description || "", category: type.category || "" });
  };

  const cancelEdit = () => { setEditId(null); };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/software-types/${editId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Actualizado");
      setEditId(null);
      fetchTypes();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este tipo de software?")) return;
    try {
      await axios.delete(`${API}/software-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Eliminado");
      fetchTypes();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="form-section" data-testid="software-types-management">
      <div className="flex justify-between items-center mb-4">
        <h2 className="border-0 pb-0 mb-0">Tipos de Software</h2>
        <span className="text-sm text-slate-500">{types.length} / 50</span>
      </div>

      {/* Quick Add */}
      <div className="flex gap-2 mb-6 p-4 bg-slate-50 border border-slate-200 rounded" data-testid="software-quick-add">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="input-sharp flex-1"
          placeholder="Nombre del software (Enter para agregar)"
          data-testid="software-quick-name"
        />
        <Select value={newCategory} onValueChange={setNewCategory}>
          <SelectTrigger className="input-sharp w-[200px]" data-testid="software-quick-category">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none_val">Sin categoría</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
            <SelectItem value="__new">+ Nueva categoría...</SelectItem>
          </SelectContent>
        </Select>
        <Button className="btn-primary" onClick={handleQuickAdd} data-testid="software-quick-add-btn">
          <Plus size={18} />
        </Button>
      </div>

      {/* New category input */}
      {newCategory === "__new" && (
        <div className="flex gap-2 mb-4 ml-4">
          <Input
            autoFocus
            className="input-sharp max-w-xs"
            placeholder="Nombre de nueva categoría"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                setNewCategory(e.target.value.trim());
              }
            }}
            onBlur={(e) => {
              if (e.target.value.trim()) setNewCategory(e.target.value.trim());
              else setNewCategory("");
            }}
            data-testid="software-new-category-input"
          />
          <span className="text-xs text-slate-400 self-center">Enter para confirmar</span>
        </div>
      )}

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="software-category-filter">
          <button
            onClick={() => setFilterCategory("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterCategory === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            data-testid="filter-all"
          >
            Todos ({types.length})
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterCategory === cat ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
              data-testid={`filter-${cat}`}
            >
              {cat} ({types.filter(t => t.category === cat).length})
            </button>
          ))}
          <button
            onClick={() => setFilterCategory("Sin categoría")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterCategory === "Sin categoría" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            data-testid="filter-uncategorized"
          >
            Sin categoría ({types.filter(t => !t.category).length})
          </button>
        </div>
      )}

      {/* Grouped List */}
      <div className="space-y-6">
        {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
          <div key={category}>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold flex items-center gap-2">
              <Wrench size={14} />
              {category}
              <span className="text-slate-400 font-normal">({items.length})</span>
            </h3>
            <div className="space-y-1">
              {items.map(type => (
                <div key={type.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 hover:border-slate-300 transition-colors group" data-testid={`software-type-${type.id}`}>
                  {editId === type.id ? (
                    <>
                      <Input
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="input-sharp flex-1 h-8 text-sm"
                        data-testid={`edit-software-name-${type.id}`}
                      />
                      <Input
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        className="input-sharp flex-1 h-8 text-sm"
                        placeholder="Descripción"
                        data-testid={`edit-software-desc-${type.id}`}
                      />
                      <Select value={editData.category || "none_val"} onValueChange={(v) => setEditData({ ...editData, category: v === "none_val" ? "" : v })}>
                        <SelectTrigger className="input-sharp w-[150px] h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none_val">Sin categoría</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit} data-testid={`save-edit-software-${type.id}`}>
                        <Check size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-3" onClick={cancelEdit}>
                        <X size={14} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-900 text-sm">{type.name}</span>
                        {type.description && <span className="text-xs text-slate-400 ml-2">— {type.description}</span>}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" onClick={() => startEdit(type)} data-testid={`edit-software-${type.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(type.id)} data-testid={`delete-software-${type.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {types.length === 0 && (
        <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200">
          <Wrench size={48} className="mx-auto mb-2 opacity-50" />
          <p>No hay tipos de software configurados</p>
          <p className="text-xs mt-1">Usa el campo de arriba para agregar uno rápidamente</p>
        </div>
      )}
    </div>
  );
};

const HardwareTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: "", description: "", category: "" });
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const { token } = useAuth();

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/hardware-types`);
      setTypes(response.data);
    } catch (error) {
      toast.error("Error al cargar tipos de hardware");
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const categories = [...new Set(types.map(t => t.category).filter(Boolean))];
  const filtered = filterCategory === "all" ? types : types.filter(t => (t.category || "Sin categoría") === filterCategory);
  const grouped = filtered.reduce((acc, t) => {
    const cat = t.category || "Sin categoría";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const handleQuickAdd = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/hardware-types`, { name: newName.trim(), category: newCategory && newCategory !== "none_val" ? newCategory : null, description: "" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hardware agregado");
      setNewName("");
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear");
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleQuickAdd(); };

  const startEdit = (type) => {
    setEditId(type.id);
    setEditData({ name: type.name, description: type.description || "", category: type.category || "" });
  };

  const cancelEdit = () => { setEditId(null); };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/hardware-types/${editId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Actualizado");
      setEditId(null);
      fetchTypes();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este tipo de hardware?")) return;
    try {
      await axios.delete(`${API}/hardware-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Eliminado");
      fetchTypes();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="form-section" data-testid="hardware-types-management">
      <div className="flex justify-between items-center mb-4">
        <h2 className="border-0 pb-0 mb-0">Tipos de Hardware</h2>
        <span className="text-sm text-slate-500">{types.length} / 100</span>
      </div>

      {/* Quick Add */}
      <div className="flex gap-2 mb-6 p-4 bg-slate-50 border border-slate-200 rounded" data-testid="hardware-quick-add">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="input-sharp flex-1"
          placeholder="Nombre del hardware (Enter para agregar)"
          data-testid="hardware-quick-name"
        />
        <Select value={newCategory} onValueChange={setNewCategory}>
          <SelectTrigger className="input-sharp w-[200px]" data-testid="hardware-quick-category">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none_val">Sin categoría</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
            <SelectItem value="__new">+ Nueva categoría...</SelectItem>
          </SelectContent>
        </Select>
        <Button className="btn-primary" onClick={handleQuickAdd} data-testid="hardware-quick-add-btn">
          <Plus size={18} />
        </Button>
      </div>

      {/* New category input */}
      {newCategory === "__new" && (
        <div className="flex gap-2 mb-4 ml-4">
          <Input
            autoFocus
            className="input-sharp max-w-xs"
            placeholder="Nombre de nueva categoría"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) setNewCategory(e.target.value.trim());
            }}
            onBlur={(e) => {
              if (e.target.value.trim()) setNewCategory(e.target.value.trim());
              else setNewCategory("");
            }}
            data-testid="hardware-new-category-input"
          />
          <span className="text-xs text-slate-400 self-center">Enter para confirmar</span>
        </div>
      )}

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="hardware-category-filter">
          <button
            onClick={() => setFilterCategory("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterCategory === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            data-testid="hw-filter-all"
          >
            Todos ({types.length})
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterCategory === cat ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
              data-testid={`hw-filter-${cat}`}
            >
              {cat} ({types.filter(t => t.category === cat).length})
            </button>
          ))}
          <button
            onClick={() => setFilterCategory("Sin categoría")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterCategory === "Sin categoría" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            data-testid="hw-filter-uncategorized"
          >
            Sin categoría ({types.filter(t => !t.category).length})
          </button>
        </div>
      )}

      {/* Grouped List */}
      <div className="space-y-6">
        {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
          <div key={category}>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold flex items-center gap-2">
              <Cpu size={14} />
              {category}
              <span className="text-slate-400 font-normal">({items.length})</span>
            </h3>
            <div className="space-y-1">
              {items.map(type => (
                <div key={type.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 hover:border-slate-300 transition-colors group" data-testid={`hardware-type-${type.id}`}>
                  {editId === type.id ? (
                    <>
                      <Input
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="input-sharp flex-1 h-8 text-sm"
                        data-testid={`edit-hardware-name-${type.id}`}
                      />
                      <Input
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        className="input-sharp flex-1 h-8 text-sm"
                        placeholder="Descripción"
                        data-testid={`edit-hardware-desc-${type.id}`}
                      />
                      <Select value={editData.category || "none_val"} onValueChange={(v) => setEditData({ ...editData, category: v === "none_val" ? "" : v })}>
                        <SelectTrigger className="input-sharp w-[150px] h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none_val">Sin categoría</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit} data-testid={`save-edit-hardware-${type.id}`}>
                        <Check size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-3" onClick={cancelEdit}>
                        <X size={14} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-900 text-sm">{type.name}</span>
                        {type.description && <span className="text-xs text-slate-400 ml-2">— {type.description}</span>}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" onClick={() => startEdit(type)} data-testid={`edit-hardware-${type.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(type.id)} data-testid={`delete-hardware-${type.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {types.length === 0 && (
        <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200">
          <Cpu size={48} className="mx-auto mb-2 opacity-50" />
          <p>No hay tipos de hardware configurados</p>
          <p className="text-xs mt-1">Usa el campo de arriba para agregar uno rápidamente</p>
        </div>
      )}
    </div>
  );
};

const StatusTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newType, setNewType] = useState({ name: "", label: "", color: "#3B82F6" });
  const { token } = useAuth();

  const colorOptions = [
    { value: "#10B981", label: "Verde" },
    { value: "#F59E0B", label: "Amarillo" },
    { value: "#EF4444", label: "Rojo" },
    { value: "#3B82F6", label: "Azul" },
    { value: "#6366F1", label: "Índigo" },
    { value: "#8B5CF6", label: "Violeta" },
    { value: "#EC4899", label: "Rosa" },
    { value: "#64748B", label: "Gris" },
  ];

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/status-types`);
      setTypes(response.data);
    } catch (error) {
      toast.error("Error al cargar tipos de estatus");
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/status-types`, { ...newType, order: types.length + 1 }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Estatus creado");
      setIsDialogOpen(false);
      setNewType({ name: "", label: "", color: "#3B82F6" });
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear estatus");
    }
  };

  const handleDelete = async (id, isDefault) => {
    if (isDefault) {
      toast.error("No se pueden eliminar estatus predeterminados");
      return;
    }
    if (!window.confirm("¿Eliminar este estatus?")) return;
    try {
      await axios.delete(`${API}/status-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Estatus eliminado");
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="form-section" data-testid="status-types-management">
      <div className="flex justify-between items-center mb-6">
        <h2 className="border-0 pb-0 mb-0">Tipos de Estatus</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-status-type-button">
              <Plus size={18} className="mr-2" /> Nuevo Estatus
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Estatus</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Identificador (sin espacios) *</Label>
                <Input
                  value={newType.name}
                  onChange={(e) => setNewType({ ...newType, name: e.target.value.toLowerCase().replace(/\s/g, "_") })}
                  className="input-sharp mt-1"
                  placeholder="Ej: pendiente_revision"
                  data-testid="new-status-name"
                />
              </div>
              <div>
                <Label>Etiqueta visible *</Label>
                <Input
                  value={newType.label}
                  onChange={(e) => setNewType({ ...newType, label: e.target.value })}
                  className="input-sharp mt-1"
                  placeholder="Ej: Pendiente de Revisión"
                  data-testid="new-status-label"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {colorOptions.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewType({ ...newType, color: opt.value })}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${newType.color === opt.value ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: opt.value }}
                      title={opt.label}
                      data-testid={`color-${opt.value}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="btn-secondary">Cancelar</Button>
              </DialogClose>
              <Button onClick={handleCreate} className="btn-primary" data-testid="create-status-submit">
                Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="space-y-3">
        {types.map(type => (
          <div 
            key={type.id} 
            className="border border-slate-200 p-4 bg-white flex justify-between items-center" 
            data-testid={`status-type-${type.id}`}
          >
            <div className="flex items-center gap-4">
              <div 
                className="w-6 h-6 rounded-full" 
                style={{ backgroundColor: type.color }}
              />
              <div>
                <span className="font-medium text-slate-900">{type.label}</span>
                <span className="text-xs text-slate-400 ml-2 font-mono">({type.name})</span>
                {type.is_default && (
                  <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5">Predeterminado</span>
                )}
              </div>
            </div>
            {!type.is_default && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleDelete(type.id, type.is_default)}
                className="text-red-600 hover:bg-red-50"
                data-testid={`delete-status-${type.id}`}
              >
                <Trash size={16} />
              </Button>
            )}
          </div>
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-slate-50 border border-slate-200">
        <h4 className="font-medium text-slate-700 mb-2">Colores de estatus:</h4>
        <ul className="text-sm text-slate-600 space-y-1">
          <li><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>Verde - Resuelto</li>
          <li><span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>Amarillo - En Proceso</li>
          <li><span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-2"></span>Rojo - No Solucionado</li>
          <li><span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>Azul - Nuevo / Otro</li>
        </ul>
      </div>
    </div>
  );
};
const PolicyTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", priority_level: 1, response_time_minutes: 60, color: "#3B82F6" });
  const { token } = useAuth();

  const colorOptions = [
    { value: "#10B981", label: "Verde" },
    { value: "#F59E0B", label: "Amarillo" },
    { value: "#EF4444", label: "Rojo" },
    { value: "#3B82F6", label: "Azul" },
    { value: "#6366F1", label: "Índigo" },
    { value: "#8B5CF6", label: "Violeta" },
    { value: "#EC4899", label: "Rosa" },
    { value: "#F97316", label: "Naranja" },
  ];

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/policy-types`);
      setTypes(response.data);
    } catch (error) {
      toast.error("Error al cargar tipos de póliza");
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const openCreate = () => {
    setEditId(null);
    setFormData({ name: "", description: "", priority_level: 1, response_time_minutes: 60, color: "#3B82F6" });
    setIsDialogOpen(true);
  };

  const openEdit = (type) => {
    setEditId(type.id);
    setFormData({ name: type.name, description: type.description || "", priority_level: type.priority_level, response_time_minutes: type.response_time_minutes, color: type.color });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await axios.put(`${API}/policy-types/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Póliza actualizada");
      } else {
        await axios.post(`${API}/policy-types`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Póliza creada");
      }
      setIsDialogOpen(false);
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar póliza");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta póliza?")) return;
    try {
      await axios.delete(`${API}/policy-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Póliza eliminada");
      fetchTypes();
    } catch (error) {
      toast.error("Error al eliminar póliza");
    }
  };

  return (
    <div className="form-section" data-testid="policy-types-management">
      <div className="flex justify-between items-center mb-6">
        <h2 className="border-0 pb-0 mb-0">Tipos de Póliza</h2>
        <Button className="btn-primary" onClick={openCreate} data-testid="add-policy-type-button">
          <Plus size={18} className="mr-2" /> Nueva Póliza
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Póliza" : "Crear Tipo de Póliza"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-sharp mt-1"
                placeholder="Ej: Póliza Premium, Póliza Básica"
                data-testid="policy-name-input"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-sharp mt-1"
                placeholder="Descripción de la póliza..."
                data-testid="policy-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nivel de Prioridad (1-10)</Label>
                <Input
                  type="number"
                  value={formData.priority_level}
                  onChange={(e) => setFormData({ ...formData, priority_level: parseInt(e.target.value) || 1 })}
                  className="input-sharp mt-1"
                  min="1"
                  max="10"
                  data-testid="policy-priority-input"
                />
              </div>
              <div>
                <Label>Tiempo Respuesta (min)</Label>
                <Input
                  type="number"
                  value={formData.response_time_minutes}
                  onChange={(e) => setFormData({ ...formData, response_time_minutes: parseInt(e.target.value) || 60 })}
                  className="input-sharp mt-1"
                  min="1"
                  data-testid="policy-response-time-input"
                />
              </div>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {colorOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: opt.value })}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${formData.color === opt.value ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: opt.value }}
                    title={opt.label}
                    data-testid={`policy-color-${opt.value}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="btn-secondary">Cancelar</Button>
            </DialogClose>
            <Button onClick={handleSave} className="btn-primary" data-testid="save-policy-submit">
              {editId ? "Guardar Cambios" : "Crear Póliza"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        {types.map(type => (
          <div key={type.id} className="border border-slate-200 p-4 bg-white flex justify-between items-center" data-testid={`policy-type-${type.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-full" style={{ backgroundColor: type.color }} />
              <div>
                <span className="font-medium text-slate-900">{type.name}</span>
                {type.description && <p className="text-xs text-slate-500 mt-0.5">{type.description}</p>}
                <div className="flex gap-3 mt-1 text-xs text-slate-500">
                  <span>Prioridad: {type.priority_level}</span>
                  <span>Respuesta: {type.response_time_minutes} min</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => openEdit(type)} className="text-blue-600 hover:bg-blue-50" data-testid={`edit-policy-${type.id}`}>
                <PencilSimple size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(type.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-policy-${type.id}`}>
                <Trash size={16} />
              </Button>
            </div>
          </div>
        ))}
        {types.length === 0 && (
          <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200">
            <Shield size={48} className="mx-auto mb-2 opacity-50" />
            <p>No hay pólizas configuradas</p>
          </div>
        )}
      </div>
      <p className="text-sm text-slate-500 mt-4">Total: {types.length} / 10 tipos de póliza</p>
    </div>
  );
};


export { UsersManagement, SoftwareTypesManagement, HardwareTypesManagement, StatusTypesManagement, PolicyTypesManagement };
